import { useRef } from 'react';

import useDebounce from './useDebounce';

export const AUTOCOMPLETE_DEBOUNCE_MS = 350;

interface UseAutocompleteSearchResult {
  /** Texto tipeado, sin espacios sobrantes. */
  trimmedSearch: string;
  /** Texto con el que se debe consultar al backend. */
  debouncedSearch: string;
  /** true mientras el debounce todavia no alcanzo al texto tipeado. */
  isDebouncePending: boolean;
  /** true cuando corresponde disparar la busqueda. */
  shouldSearch: boolean;
}

/**
 * Normaliza el termino de busqueda de un autocomplete: recorta, aplica debounce
 * y solo habilita la consulta cuando el texto estabilizado supera minLength.
 * Evita una request por tecla y respuestas que llegan fuera de orden.
 */
export const useAutocompleteSearch = (
  search?: string,
  minLength = 3,
  delay = AUTOCOMPLETE_DEBOUNCE_MS
): UseAutocompleteSearchResult => {
  const trimmedSearch = (search ?? '').trim();
  const debouncedSearch = useDebounce(trimmedSearch, delay);
  const isDebouncePending = debouncedSearch !== trimmedSearch;

  return {
    trimmedSearch,
    debouncedSearch,
    isDebouncePending,
    shouldSearch: debouncedSearch.length >= minLength && !isDebouncePending,
  };
};

interface UseStableOptionsParams {
  /** true mientras hay una busqueda en vuelo o pendiente de debounce. */
  isPending: boolean;
  /** false cuando no corresponde buscar: se devuelve lista vacia. */
  enabled: boolean;
}

/**
 * Mantiene visibles las ultimas opciones resueltas mientras entra la siguiente
 * busqueda, en lugar de vaciar la lista en cada tecla.
 */
export const useStableOptions = <T,>(
  options: T[],
  { isPending, enabled }: UseStableOptionsParams
): T[] => {
  const lastResolvedOptionsRef = useRef<T[]>([]);

  if (!enabled) {
    lastResolvedOptionsRef.current = [];
    return [];
  }

  if (!isPending) {
    lastResolvedOptionsRef.current = options;
    return options;
  }

  return lastResolvedOptionsRef.current;
};
